import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/lib/theme";
import * as React from "react";

import { ApiError } from "@/lib/api/client";
import { forceLogout } from "@/lib/api/session";

// A 401 from any query or mutation means the admin session expired or is
// invalid → log the user out globally (not just on the current call).
function handleAuthError(error: unknown): void {
  if (error instanceof ApiError && error.status === 401) void forceLogout("expired");
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [client] = React.useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({ onError: handleAuthError }),
        mutationCache: new MutationCache({ onError: handleAuthError }),
        defaultOptions: {
          queries: {
            staleTime: 15_000,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              // Never retry auth / client errors; retry transient ones twice.
              if (error instanceof ApiError && error.status > 0 && error.status < 500) return false;
              return failureCount < 2;
            },
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={client}>
      <ThemeProvider defaultTheme="dark">{children}</ThemeProvider>
    </QueryClientProvider>
  );
}
